import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * Every button in the design is Oswald, uppercase and letter-spaced — the
 * "site signage" voice. These are the treatments that actually appear in
 * `Visual - Demo.dc.html`:
 *
 *   primary    burnt-orange fill + brand glow        "Add crew member"
 *   secondary  white with the signature 1.5px border "Export roster"
 *   outline    white with a *brand* 1.5px border     "Start daily capture"
 *   success    green fill                            "Accept decision"
 *   ghost      no chrome, orange label               "Clear filter"
 *   dark       chrome fill                           modal confirms
 *   onDark     translucent white, for dark overlays  scan-flow "Cancel"
 *
 * Note the design uses two different orange hovers and they are not
 * interchangeable: #FF6A1A (brand-hover) on app chrome and modals, #B8440A
 * (brand-deep) on the project-setup wizard. `primary` ships the first; the
 * wizard passes `hover:bg-brand-deep`.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-display uppercase tracking-[0.04em] cursor-pointer transition-colors",
    "disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary:
          "rounded-input border-none bg-brand font-bold text-white shadow-brand hover:bg-brand-hover",
        secondary:
          "rounded-input border-[1.5px] border-border bg-surface font-semibold text-ink-slate hover:border-border-hover hover:bg-subtle",
        outline:
          "rounded-input border-[1.5px] border-brand bg-surface font-bold text-brand hover:bg-brand-tint",
        success:
          "rounded-input border-none bg-ok font-bold text-white shadow-[0_1px_2px_rgb(20_22_27/0.06),0_6px_16px_rgb(10_125_79/0.18)] hover:bg-ok-strong",
        ghost:
          "rounded-input border-none bg-transparent font-semibold tracking-[0.02em] text-brand hover:bg-brand-tint-3",
        dark: "rounded-input border-none bg-chrome font-bold text-white hover:bg-ink-soft",
        onDark:
          "rounded-input border-[1.5px] border-white/30 bg-white/10 font-semibold tracking-[0.03em] text-white hover:bg-white/22",
      },
      size: {
        /** Compact inline actions — "Clear filter", small "Add" buttons. */
        sm: "min-h-[34px] rounded-control px-3 text-[0.8rem]",
        /** The toolbar height used throughout the app. */
        md: "min-h-[46px] px-[18px] text-[0.92rem]",
        /** Modal footer actions. */
        footer: "min-h-12 px-[18px] text-[0.92rem]",
        /** Tall call-to-action — "Start daily capture", modal confirms. */
        lg: "min-h-[50px] gap-[9px] px-6 text-[0.96rem]",
        /** Square icon-only — the mic on the Site Brain hero. */
        icon: "size-[46px] gap-0 px-0",
      },
    },
    compoundVariants: [
      // The filled toolbar button is 2px wider on each side than the outlined
      // one, so the heavier fill reads as the same optical size.
      { variant: "primary", size: "md", className: "px-5" },
    ],
    defaultVariants: { variant: "primary", size: "md" },
  },
);

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    /** Render the child element instead of a `<button>`, keeping the styling. */
    asChild?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
export type { ButtonProps };
